import React, { useEffect, useState } from 'react';
import Button from '../../../composants/Button/Button';
import CompteARebourd from '../../../composants/CompteARebourd/CompteARebourd';
import { OperationMS } from '../types';
import { DechiffrageInfo, countPlisRecus } from '../domain/analyse/historique';
import { formatDateHeure } from '../domain/analyse/dates';
import { getQuestionsReponsesCount } from '../services/operationMsService';

// Section 1 — Reception des plis. Recap d'etat : date limite, duree restante (pendant la
// consultation), date/operateur de dechiffrage, nombre d'echanges Q/R. Le bouton "Dechiffrer"
// delegue l'ouverture des plis au parent. L'analyse IA des offres est declenchee separement
// depuis la section "Analyse des offres techniques" — plus de case IA ici.
interface OperationAnalyseReceptionProps {
  operation: OperationMS;
  isConsultation: boolean;
  dechiffrage: DechiffrageInfo;
  onDechiffrer: () => void;
  dechiffrerDisabled?: boolean;
  dechiffrerHint?: string;
  onGoToQuestionsReponses?: () => void;
}

const PLACEHOLDER = '—';

const OperationAnalyseReception: React.FC<OperationAnalyseReceptionProps> = ({
  operation,
  isConsultation,
  dechiffrage,
  onDechiffrer,
  dechiffrerDisabled = false,
  dechiffrerHint,
  onGoToQuestionsReponses,
}) => {
  // Date limite figee a `date_limite_remise` ; fallback `expire_at` (deadline vivante, nullable
  // apres consultation). NB : `date_fin_operation` = fin d'operation prestataire (≠ deadline).
  const dateLimite = formatDateHeure(operation.date_limite_remise ?? operation.expire_at);
  const dateDechiffrage = formatDateHeure(dechiffrage.date);
  // Plis recus = prestataires ayant soumis leur offre (progression de la consultation).
  const nbPlisRecus = countPlisRecus(operation.historique);

  // Nombre de messages du canal Questions/reponses (meme source que l'onglet Q/R).
  const [nbEchanges, setNbEchanges] = useState<number | null>(null);
  useEffect(() => {
    const id = operation.id;
    if (!id) {
      setNbEchanges(null);
      return undefined;
    }
    let cancelled = false;
    getQuestionsReponsesCount(id)
      .then((count) => {
        if (!cancelled) setNbEchanges(count);
      })
      .catch(() => {
        if (!cancelled) setNbEchanges(null);
      });
    return () => {
      cancelled = true;
    };
  }, [operation.id]);

  return (
    <div className="opms__reception">
      <div className="opms__reception-grid">
        <div className="opms__reception-fields">
          <div className="opms__reception-row">
            <span className="opms__reception-label">Date limite de réception des plis</span>
            <span className="opms__reception-value">
              {dateLimite ? `${dateLimite} (heure de Paris)` : PLACEHOLDER}
            </span>
          </div>
          <div className="opms__reception-row">
            <span className="opms__reception-label">Durée restante</span>
            <span className="opms__reception-value">
              {isConsultation
                ? operation.expire_at
                  ? <CompteARebourd targetDate={operation.expire_at} autoReload={false} source="MS" />
                  : PLACEHOLDER
                : 'Consultation terminée'}
            </span>
          </div>
          <div className="opms__reception-row">
            <span className="opms__reception-label">Date d'ouverture et de déchiffrage des plis</span>
            <span className="opms__reception-value">
              {dateDechiffrage
                ? dateDechiffrage
                : isConsultation
                ? 'Plis scellés — non déchiffrés'
                : 'Plis non encore ouverts'}
            </span>
          </div>
          <div className="opms__reception-row">
            <span className="opms__reception-label">Nombre de plis reçus</span>
            <span className="opms__reception-value">{nbPlisRecus}</span>
          </div>
          <div className="opms__reception-row">
            <span className="opms__reception-label">Nombre d'échanges Questions/réponses</span>
            <span className="opms__reception-value">
              {nbEchanges === null ? (
                PLACEHOLDER
              ) : onGoToQuestionsReponses ? (
                <button
                  type="button"
                  className="opms__reception-link"
                  onClick={onGoToQuestionsReponses}
                  title="Voir les échanges dans l'onglet Questions/réponses"
                >
                  {nbEchanges}
                </button>
              ) : (
                nbEchanges
              )}
            </span>
          </div>
          <div className="opms__reception-row">
            <span className="opms__reception-label">Déchiffrage opéré par</span>
            <span className="opms__reception-value">{dechiffrage.operateur || PLACEHOLDER}</span>
          </div>
        </div>

        <div className="opms__reception-actions">
          <Button
            value="Déchiffrer et ouvrir les plis"
            variant="primary"
            disabled={dechiffrerDisabled}
            onClick={() => onDechiffrer()}
          />
          {dechiffrerHint && <p className="opms__reception-hint">{dechiffrerHint}</p>}
        </div>
      </div>
    </div>
  );
};

export default OperationAnalyseReception;
